import { useState, useRef, useCallback, useEffect, memo } from 'react';
import {
  Trash2,
  Copy,
  Lock,
  Unlock,
  Monitor,
  Smartphone,
  Tablet,
  Edit3,
  ChevronDown,
} from 'lucide-react';
import type { DeviceFrame, DeviceType, WhiteboardElement } from '../../types/whiteboard';
import { DEVICE_FRAME_PRESETS } from '../../types/whiteboard';
import { WhiteboardElementRenderer } from './WhiteboardElementRenderer';

interface Props {
  frame: DeviceFrame;
  isSelected: boolean;
  isFocused: boolean; // "entered" into the frame
  childElements: WhiteboardElement[];
  selectedElementIds: string[];
  zoom: number;
  activeTool: string | null;
  highlightDrop: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<DeviceFrame>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onChangeDevice: (deviceType: DeviceType) => void;
  onElementSelect: (id: string) => void;
  onElementUpdate: (id: string, updates: Partial<WhiteboardElement>) => void;
  onElementDelete: (id: string) => void;
  onElementDuplicate: (element: WhiteboardElement) => void;
}

const deviceTypeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  phone: Smartphone,
  tablet: Tablet,
  desktop: Monitor,
};

export const WhiteboardFrameRenderer = memo(function WhiteboardFrameRenderer({
  frame,
  isSelected,
  isFocused,
  childElements,
  selectedElementIds,
  zoom,
  activeTool: _activeTool,
  highlightDrop,
  onSelect,
  onUpdate,
  onDelete,
  onDuplicate,
  onChangeDevice,
  onElementSelect,
  onElementUpdate,
  onElementDelete,
  onElementDuplicate,
}: Props) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, frameX: 0, frameY: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [isEditingName, setIsEditingName] = useState(false);
  const [showDeviceMenu, setShowDeviceMenu] = useState(false);

  const preset = frame.deviceType !== 'custom' ? DEVICE_FRAME_PRESETS[frame.deviceType] : null;
  const chromeType = preset?.chromeType ?? 'desktop';
  const statusBarHeight = chromeType === 'phone' ? 44 : 0;

  // Drag handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (frame.locked) return;
    e.stopPropagation();
    onSelect();

    // Don't drag from resize handles or inner viewport
    const target = e.target as HTMLElement;
    if (target.closest('.resize-handle') || target.closest('[data-element-id]')) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      frameX: frame.x,
      frameY: frame.y,
    });
  }, [frame, onSelect]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging && !frame.locked) {
      const dx = (e.clientX - dragStart.x) / zoom;
      const dy = (e.clientY - dragStart.y) / zoom;
      onUpdate({
        x: Math.round(dragStart.frameX + dx),
        y: Math.round(dragStart.frameY + dy),
      });
    }
    if (isResizing) {
      const dx = (e.clientX - dragStart.x) / zoom;
      const dy = (e.clientY - dragStart.y) / zoom;
      const newW = Math.max(200, resizeStart.w + dx);
      const newH = Math.max(200, resizeStart.h + dy);

      if (e.shiftKey && preset) {
        // Maintain aspect ratio
        const aspect = preset.viewportWidth / preset.viewportHeight;
        const wFromH = newH * aspect;
        onUpdate({ viewportWidth: Math.round(wFromH), viewportHeight: Math.round(newH) });
      } else {
        onUpdate({ viewportWidth: Math.round(newW), viewportHeight: Math.round(newH) });
      }
    }
  }, [isDragging, isResizing, dragStart, resizeStart, frame, zoom, onUpdate, preset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (frame.locked) return;
    e.stopPropagation();
    setIsResizing(true);
    setDragStart({ x: e.clientX, y: e.clientY, frameX: frame.x, frameY: frame.y });
    setResizeStart({ x: e.clientX, y: e.clientY, w: frame.viewportWidth, h: frame.viewportHeight });
  }, [frame]);

  // Scroll inside frame
  const handleViewportWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    const maxScroll = Math.max(0, getContentHeight() - frame.viewportHeight);
    const newOffset = Math.max(0, Math.min(maxScroll, frame.scrollOffset + e.deltaY));
    onUpdate({ scrollOffset: newOffset });
  }, [frame, onUpdate]);

  // Calculate content height from children
  const getContentHeight = useCallback(() => {
    if (childElements.length === 0) return frame.viewportHeight;
    const maxBottom = Math.max(
      ...childElements.map(el => (el.relativeY ?? el.y) + el.height)
    );
    return Math.max(frame.viewportHeight, maxBottom + 16);
  }, [childElements, frame.viewportHeight]);

  const scrollRatio = frame.viewportHeight / getContentHeight();
  const showScrollbar = scrollRatio < 1;

  const DeviceIcon = deviceTypeIcons[chromeType] || Monitor;

  const deviceOptions: { type: DeviceType; label: string }[] = [
    { type: 'iphone_15', label: 'iPhone 15 / 16' },
    { type: 'iphone_se', label: 'iPhone SE' },
    { type: 'android', label: 'Android (Pixel)' },
    { type: 'ipad', label: 'iPad' },
    { type: 'desktop', label: 'Desktop' },
    { type: 'desktop_wide', label: 'Desktop Wide' },
    { type: 'custom', label: 'Custom' },
  ];

  return (
    <div
      ref={frameRef}
      data-frame-id={frame.id}
      className={`
        absolute group
        ${isSelected ? 'z-40' : ''}
        ${isDragging ? 'cursor-grabbing' : frame.locked ? 'cursor-not-allowed' : 'cursor-grab'}
      `}
      style={{
        left: frame.x,
        top: frame.y,
        width: frame.viewportWidth,
        zIndex: frame.zIndex,
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Title bar */}
      <div
        className={`
          h-9 flex items-center gap-2 px-3 border border-b-0 rounded-t-xl
          ${isSelected ? 'bg-primary-dim border-primary/30' : 'bg-surface-2 border-default'}
          transition-colors
        `}
      >
        <DeviceIcon className="w-3.5 h-3.5 text-muted flex-shrink-0" />
        {isEditingName ? (
          <input
            type="text"
            value={frame.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            onBlur={() => setIsEditingName(false)}
            onKeyDown={(e) => e.key === 'Enter' && setIsEditingName(false)}
            className="bg-transparent text-xs font-medium text-default outline-none flex-1 min-w-0"
            autoFocus
          />
        ) : (
          <span
            className="text-xs font-medium text-muted truncate flex-1 cursor-text"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setIsEditingName(true);
            }}
          >
            {frame.name}
          </span>
        )}
        <span className="text-[10px] text-subtle flex-shrink-0">
          {frame.viewportWidth} x {frame.viewportHeight}
        </span>
      </div>

      {/* Phone status bar (notch) */}
      {chromeType === 'phone' && (
        <div
          className="flex items-center justify-between px-6 border-x"
          style={{
            height: statusBarHeight,
            backgroundColor: '#1a1a1a',
            borderColor: isSelected ? 'var(--accent-600)' : 'var(--neutral-200)',
          }}
        >
          <span className="text-[10px] text-white/70 font-medium">9:41</span>
          <div className="w-28 h-6 bg-black rounded-full" /> {/* Notch / Dynamic Island */}
          <div className="flex items-center gap-1">
            <div className="w-4 h-2 border border-white/60 rounded-sm">
              <div className="w-2.5 h-full bg-white/60 rounded-sm" />
            </div>
          </div>
        </div>
      )}

      {/* Viewport area */}
      <div
        className={`
          relative overflow-hidden border
          ${chromeType === 'phone' ? '' : 'rounded-b-xl'}
          ${isSelected ? 'border-primary/30' : 'border-default'}
          ${highlightDrop ? 'ring-2 ring-primary/30 bg-primary/5' : ''}
          transition-all
        `}
        style={{
          width: frame.viewportWidth,
          height: frame.viewportHeight,
          backgroundColor: frame.backgroundColor,
        }}
        onWheel={handleViewportWheel}
      >
        {/* Scrollable content layer */}
        <div
          className="absolute inset-x-0"
          style={{
            top: -frame.scrollOffset,
          }}
        >
          {/* Render child elements */}
          {childElements.map(element => (
            <div key={element.id} className="pointer-events-auto">
              <WhiteboardElementRenderer
                element={{
                  ...element,
                  x: element.relativeX ?? element.x,
                  y: element.relativeY ?? element.y,
                }}
                isSelected={selectedElementIds.includes(element.id)}
                zoom={zoom}
                onSelect={() => onElementSelect(element.id)}
                onUpdate={(updates) => {
                  // Clamp to frame bounds
                  const newUpdates = { ...updates };
                  if (newUpdates.x !== undefined) {
                    newUpdates.relativeX = Math.max(0, Math.min(frame.viewportWidth - element.width, newUpdates.x));
                    newUpdates.x = newUpdates.relativeX;
                  }
                  if (newUpdates.y !== undefined) {
                    newUpdates.relativeY = Math.max(0, newUpdates.y);
                    newUpdates.y = newUpdates.relativeY;
                  }
                  onElementUpdate(element.id, newUpdates);
                }}
                onDelete={() => onElementDelete(element.id)}
                onDuplicate={() => onElementDuplicate(element)}
              />
            </div>
          ))}

          {/* Empty state inside frame */}
          {childElements.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p className="text-xs text-muted">
                  Click a component tool, then click here
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Scroll indicator */}
        {showScrollbar && (
          <div className="absolute right-1 top-1 bottom-1 w-1.5 rounded-full bg-black/5">
            <div
              className="w-full rounded-full bg-black/20 hover:bg-black/30 transition-colors"
              style={{
                height: `${scrollRatio * 100}%`,
                marginTop: `${(frame.scrollOffset / getContentHeight()) * 100}%`,
              }}
            />
          </div>
        )}
      </div>

      {/* Phone bottom bar */}
      {chromeType === 'phone' && (
        <div
          className="flex items-center justify-center border border-t-0 rounded-b-xl"
          style={{
            height: 20,
            backgroundColor: frame.backgroundColor,
            borderColor: isSelected ? 'var(--accent-600)' : 'var(--neutral-200)',
          }}
        >
          <div className="w-24 h-1 bg-black/20 rounded-full" /> {/* Home indicator */}
        </div>
      )}

      {/* Selection ring */}
      {isSelected && (
        <div className="absolute -inset-1 border-2 border-primary rounded-xl pointer-events-none" />
      )}

      {/* Focus ring (entered into frame) */}
      {isFocused && !isSelected && (
        <div className="absolute -inset-1 border-2 border-primary/40 rounded-xl pointer-events-none border-dashed" />
      )}

      {/* Resize handle (bottom-right) */}
      {isSelected && !frame.locked && (
        <>
          <div
            className="resize-handle absolute -bottom-2 -right-2 w-4 h-4 bg-white border-2 border-primary rounded-sm cursor-se-resize z-50"
            onMouseDown={handleResizeStart}
          />
          <div
            className="resize-handle absolute -bottom-2 -left-2 w-4 h-4 bg-white border-2 border-primary rounded-sm cursor-sw-resize z-50"
            onMouseDown={handleResizeStart}
          />
        </>
      )}

      {/* Quick actions toolbar */}
      {isSelected && !frame.locked && (
        <div className="absolute -top-[52px] left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-1.5 bg-surface rounded-lg shadow-lg border border-default z-50">
          <button
            onClick={(e) => { e.stopPropagation(); setIsEditingName(true); }}
            className="p-1.5 rounded hover:bg-surface-2 text-muted hover:text-default"
            title="Rename"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            className="p-1.5 rounded hover:bg-surface-2 text-muted hover:text-default"
            title="Duplicate"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>

          {/* Device type changer */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowDeviceMenu(!showDeviceMenu); }}
              className="p-1.5 rounded hover:bg-surface-2 text-muted hover:text-default flex items-center gap-0.5"
              title="Change device"
            >
              <DeviceIcon className="w-3.5 h-3.5" />
              <ChevronDown className="w-2.5 h-2.5" />
            </button>

            {showDeviceMenu && (
              <div className="absolute top-full left-0 mt-1 bg-surface border border-default rounded-lg shadow-lg py-1 z-50 min-w-[160px]">
                {deviceOptions.map(opt => (
                  <button
                    key={opt.type}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChangeDevice(opt.type);
                      setShowDeviceMenu(false);
                    }}
                    className={`
                      w-full text-left px-3 py-1.5 text-xs hover:bg-surface-2 transition-colors
                      ${frame.deviceType === opt.type ? 'text-primary font-medium' : 'text-default'}
                    `}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); onUpdate({ locked: !frame.locked }); }}
            className="p-1.5 rounded hover:bg-surface-2 text-muted hover:text-default"
            title={frame.locked ? 'Unlock' : 'Lock'}
          >
            {frame.locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
          </button>

          <div className="w-px h-4 bg-border" />

          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded hover:bg-danger-dim text-muted hover:text-danger"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Lock indicator */}
      {frame.locked && (
        <div className="absolute -top-2 -right-2 w-5 h-5 bg-warning rounded-full flex items-center justify-center z-50">
          <Lock className="w-3 h-3 text-white" />
        </div>
      )}
    </div>
  );
});
